import {
  rdbV1FiltersTeamsList,
  rdbV1FiltersYearsList,
  rdbV1TeamRankingOrganizationsSummaryDetail,
  rdbV2OrganizationsCommitsDetail,
} from '@on3/api';
import type { ICommitsIndexProps } from '@on3/ui-lib/src/api/schema/custom-contracts';
import { withAuthentication } from '@on3/ui-lib/src/hocs/withAuthentication';
import { withValidUrl } from '@on3/ui-lib/src/hocs/withValidUrl';
import { getFulfilledValue, proxyApi } from '@on3/ui-lib/utils/api';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { CommitList } from '@public/components/College/Interests/Commits/CommitList';
import { PageWrapper } from '@public/components/Layouts/PageWrapper';
import { createDescription, createTitle } from '@public/utils/meta';
import type { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';

const CommitsIndex = (props: ICommitsIndexProps) => {
  const { currentSite } = props?.siteData || {};

  const router = useRouter();
  const { year, sport, status } = router.query;
  const orgName =
    currentSite?.organization?.knownAs ?? currentSite?.organization?.name;
  const orgMascot = currentSite?.organization?.mascot;
  const title = createTitle(year, orgName, orgMascot, sport, 'commits');
  const description = createDescription(
    '{0} {1} {2} {3} team commit list.',
    year,
    orgName,
    orgMascot,
    sport,
  );
  const sortedList = status
    ? props?.playerList?.list?.filter(
        (item) =>
          item.status?.type?.toLowerCase() === `${status}` &&
          !item.status.transferredAsset,
      )
    : props?.playerList?.list;
  const noIndex =
    (props?.playerList?.pagination?.count || 0) === 0 ||
    sortedList?.length === 0;

  return (
    <PageWrapper
      {...props}
      description={description}
      noIndex={noIndex}
      title={title}
    >
      <CommitList
        {...props}
        key={`${year}-${sport}-${orgName}-${status}`}
        title={title}
        toggled={false}
      />
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps<
  ICommitsIndexProps | []
> = async (ctx) => {
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);

  if (!siteData) {
    return {
      notFound: true,
    };
  }

  const orgKey = siteData?.currentSite?.organizationKey;
  const { year, sport } = ctx.query;

  const { rankingSports } = siteData || {};

  const sportKey = rankingSports?.find(
    (sp) => sp?.name && slugify(sp.name) === sport,
  )?.key;

  if (!orgKey || !year || !sport || !sportKey) {
    return {
      notFound: true,
    };
  }

  try {
    const params = {
      orgKey,
      sportKey,
      year: Number(year) || undefined,
    };
    const teamRankParams = {
      organizationKey: orgKey,
      year: params.year,
      sportKey,
    };
    const teamFilterParams = {
      sportKey,
      year: params.year,
      groupBy: true,
    };
    const [playerList, teamRank, teams, years] = await Promise.allSettled([
      rdbV2OrganizationsCommitsDetail(proxyApi, params),
      rdbV1TeamRankingOrganizationsSummaryDetail(proxyApi, teamRankParams),
      rdbV1FiltersTeamsList(proxyApi, teamFilterParams),
      rdbV1FiltersYearsList(proxyApi),
    ]);

    if (playerList.status === 'rejected') {
      return {
        notFound: true,
      };
    }

    const data = {
      pageType: 'database',
      siteData,
      userData,
      playerList: getFulfilledValue(playerList),
      teamRank: getFulfilledValue(teamRank),
      teams: getFulfilledValue(teams),
      years: getFulfilledValue(years),
    } as ICommitsIndexProps;

    return { props: { ...data } };
  } catch (e) {
    return { props: [] };
  }
};

export default withValidUrl(CommitsIndex);
